import { useId, useEffect, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { CandidateListing, MarkVerdict } from "../../../api/content/curationApi";
import type { Discussion } from "../../../api/content/discussionsApi";
import type { Review } from "../../../api/content/reviewDecoders";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import TermAlignmentsPanel from "../alignment/TermAlignmentsPanel";
import BulkDecisions from "../bulk/BulkDecisions";
import CheckResultList from "../checks/CheckResultList";
import ConflictsPanel from "../conflicts/ConflictsPanel";
import { conflictRows } from "../conflicts/conflictRows";
import ConflictRowView from "../conflicts/ConflictRowView";
import type { OutlineRow } from "../outline/reviewOutline";
import ChecksPanel from "../page/ChecksPanel";
import DecisionHistory from "../page/DecisionHistory";
import TermDiscussionsPanel from "../page/TermDiscussionsPanel";
import ItemUnit from "./ItemUnit";
import TermUnit from "./TermUnit";
import TurtleEditor from "./TurtleEditor";
import { useConflictTermNames } from "../conflicts/conflictTermNames";

/**
 * The unit in focus (curation-ui.md CU-1, CU-3), and the action bar every unit has: OK
 * (`o`), Needs change (`x`, with a note that becomes a comment), Comment (`c`), Next
 * unresolved (`n`). The conversation unit is the overview: every conflict by level, the
 * checks, term alignments and decision history, so nothing of the M5 page is lost.
 */
export default function FocusPane({
  row,
  rows,
  review,
  candidates,
  discussion,
  othersHere,
  canEdit,
  canMark,
  canMarkInBulk,
  technical,
  editing,
  noteInput,
  noteRequests,
  onMark,
  onComment,
  onFocusItem,
  onEditDone,
  onChanged,
}: {
  row: OutlineRow;
  rows: readonly OutlineRow[];
  review: Review;
  candidates: CandidateListing | null;
  discussion: Discussion | null;
  /** The others who look at this unit now (CU-10 presence). */
  othersHere: readonly string[];
  canEdit: boolean;
  canMark: boolean;
  /** The viewer curates units of this open change set (CU-9). */
  canMarkInBulk: boolean;
  technical: boolean;
  /** The item whose Turtle is being edited, if any. */
  editing: string | null;
  noteInput: RefObject<HTMLInputElement | null>;
  /** Grows each time `x` is pressed: open the note. */
  noteRequests: number;
  onMark: (verdict: MarkVerdict | "cleared", note: string) => void;
  onComment: () => void;
  onFocusItem: (itemId: string) => void;
  onEditDone: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const termNames = useConflictTermNames(review.conflictCheck);
  const [note, setNote] = useState<string | null>(null);
  // `x` asks for the note; open it once per request (state adjusted while rendering).
  const [answeredRequests, setAnsweredRequests] = useState(noteRequests);
  if (answeredRequests !== noteRequests) {
    setAnsweredRequests(noteRequests);
    setNote("");
  }
  useEffect(() => {
    if (note === "") {
      noteInput.current?.focus();
    }
  }, [note, noteInput]);
  const unit = row.unit;
  const id = unit.key.slice(unit.key.indexOf(":") + 1);
  const editedItem = editing === null ? undefined : review.items.find((item) => item.itemId === editing);
  return (
    <section className="space-y-4" aria-labelledby={headingId}>
      <header className="space-y-0.5">
        <p className="app-text-muted text-xs uppercase tracking-wide">{t(`reviews.curation.outline.groups.${row.group}`)}</p>
        <h3 id={headingId} className="app-title text-lg font-semibold">
          {unit.kind === "conversation" ? t("reviews.curation.outline.conversation") : row.name}
        </h3>
        {othersHere.length > 0 ? <p className="app-status-text-warning text-xs">{t("reviews.collaboration.presence.isHere", { names: othersHere.join(", ") })}</p> : null}
      </header>
      {editedItem !== undefined ? <TurtleEditor key={editedItem.itemId} review={review} item={editedItem} onClose={onEditDone} onChanged={onChanged} /> : null}
      {unit.kind === "conversation" ? (
        <div className="space-y-4">
          <DecisionHistory review={review} />
          <BulkDecisions review={review} rows={rows} candidates={candidates} canMark={canMarkInBulk} canEdit={canEdit} onChanged={onChanged} />
          <TermDiscussionsPanel review={review} discussion={discussion} onChanged={onChanged} />
          <TermAlignmentsPanel key={review.head} changeSetId={review.changeSet.changeSetId} />
          <ConflictsPanel review={review} onFocusItem={onFocusItem} onChanged={onChanged} />
          <ChecksPanel review={review} />
        </div>
      ) : null}
      {unit.kind === "term" ? (
        <TermUnit unit={unit} group={row.group} name={row.name} review={review} candidates={candidates} canEdit={canEdit} technical={technical} onFocusItem={onFocusItem} onChanged={onChanged} />
      ) : null}
      {unit.kind === "item"
        ? (() => {
            const item = review.items.find((entry) => entry.itemId === id);
            return item === undefined ? null : <ItemUnit item={item} review={review} edits={{ canEdit, onChanged }} />;
          })()
        : null}
      {unit.kind === "affected" ? (
        <div className="space-y-2">
          <p className="text-sm">{t("reviews.curation.affectedExplained")}</p>
          <ul className="space-y-1">
            {conflictRows((review.conflictCheck?.findings ?? []).filter((finding) => finding.item?.itemId === id)).map((entry, index) => (
              <ConflictRowView
                key={String(index)}
                row={entry}
                labels={termNames}
                changedItemIds={new Set(review.items.map((item) => item.itemId))}
                onFocusItem={onFocusItem}
              />
            ))}
          </ul>
        </div>
      ) : null}
      {unit.kind === "check" ? <CheckResultList results={[...(review.checks?.results ?? []), ...(review.checks?.processResults ?? [])].filter((result) => result.checkId === id)} /> : null}
      <footer className="app-card sticky bottom-0 space-y-2 p-2" aria-label={t("reviews.curation.actionBar")}>
        <div className="flex flex-wrap items-center gap-2">
          {canMark ? (
            <>
              <AppButton size="sm" variant={row.myMark === "ok" ? "primary" : "secondary"} aria-keyshortcuts="o" onClick={() => {
                onMark(row.myMark === "ok" ? "cleared" : "ok", "");
              }}>
                {row.myMark === "ok" ? t("reviews.curation.marks.clearOk") : t("reviews.curation.marks.ok")}
              </AppButton>
              <AppButton size="sm" variant={row.myMark === "needs_change" ? "primary" : "secondary"} aria-keyshortcuts="x" onClick={() => {
                setNote(note === null ? "" : null);
              }}>
                {t("reviews.curation.marks.needsChange")}
              </AppButton>
            </>
          ) : (
            <span className="app-text-muted text-xs">{t("reviews.curation.marks.notYours")}</span>
          )}
          <AppButton size="sm" variant="ghost" aria-keyshortcuts="c" onClick={onComment}>
            {t("reviews.curation.comment")}
          </AppButton>
        </div>
        {note !== null ? (
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              onMark("needs_change", note.trim());
              setNote(null);
            }}
          >
            <input
              ref={noteInput}
              className={`${FORM_COMPACT_CONTROL_CLASS} min-w-0 flex-1`}
              aria-label={t("reviews.curation.marks.note")}
              placeholder={t("reviews.curation.marks.note")}
              value={note}
              maxLength={2000}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            <AppButton size="sm" type="submit" disabled={note.trim() === ""}>
              {t("reviews.curation.marks.sendNote")}
            </AppButton>
          </form>
        ) : null}
      </footer>
    </section>
  );
}
